import { Component, HostListener, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgSelectConfig } from '@ng-select/ng-select';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from "rxjs";
import { saveAs } from "file-saver";

import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';

import { AtribuicaoLoginSed, UserLoginSed } from 'src/app/shared/models/sed/UserLogin';
import { Atividade } from "src/app/shared/models/relatorio/Atividade";
import { Polo } from "src/app/shared/models/polo/Polo";

import { AuthService } from 'src/app/shared/auth/services/auth.service';
import { PsicologoService } from "src/app/shared/services/psicologo.service";
import { RelatorioService } from "src/app/shared/services/relatorio.service";
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';

@Component({
  selector: 'app-relatorio-atividade',
  templateUrl: './relatorio-atividade.component.html',
  styleUrls: ['./relatorio-atividade.component.scss'],
  standalone: true,
  imports: [angularCommonImports, libsCommonImports, convivaCommonImports]
})
export class RelatorioAtividadeComponent implements OnInit {
  private config = inject(NgSelectConfig);
  private spinnerService = inject(NgxSpinnerService);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);
  private ConvivaErrors = inject(ConvivaErrorHandlingService);
  public userLogado: UserLoginSed | null;
  public atribuicoesLogin: AtribuicaoLoginSed;

  private psicologoService = inject(PsicologoService);
  private relatorioService = inject(RelatorioService);

  public showModalConfirmacao = false;
  public polos: Polo[] = [];
  public relatoriosGerados: Atividade[] = [];
  public usuarioPossuiMultiplosPolos = false;

  @ViewChild('modalContent') modalContent!: TemplateRef<any>;

  public submitted = false;

  public formGerar!: FormGroup;

  public get form() {
    return this.formGerar.controls;
  }

  public get codigoPolo() {
    return this.formGerar.get('codigoPolo')!;
  }

  public get dataInicial() {
    return this.formGerar.get('dataInicial')!;
  }

  public get dataFinal() {
    return this.formGerar.get('dataFinal')!;
  }

  public colunasGrid = [
    { title: 'Data de criação', field: 'dataDeCriacao', hozAlign: "center", headerHozAlign: "center", formatter: "datetime", formatterParams: { inputFormat: "iso", outputFormat: "dd/MM/yyyy - HH:mm", } },
    { title: 'Polo', field: 'descricaoPolo', hozAlign: "center", headerHozAlign: "center" },
    { title: 'Data inicial', field: 'dataInicial', hozAlign: "center", headerHozAlign: "center", formatter: "datetime", formatterParams: { inputFormat: "iso", outputFormat: "dd/MM/yyyy", } },
    { title: 'Data final', field: 'dataFinal', hozAlign: "center", headerHozAlign: "center", formatter: "datetime", formatterParams: { inputFormat: "iso", outputFormat: "dd/MM/yyyy", } },
    {
      title: 'Status', field: 'status', hozAlign: "center", headerHozAlign: "center", formatter: (cell: any) => {
        const status = cell.getValue();
        switch (status) {
          case 1: return 'Erro';
          case 2: return 'Em andamento';
          case 3: return 'Concluído';
          default: return '';
        }
      },
    },
    {
      title: 'Ações', field: 'status', width: 110, hozAlign: "center", headerHozAlign: "center", headerSort: false, formatter: (cell: any) => {
        if (cell.getRow().getData().status == 3)
          return `
          <div style='display:inline-flex;gap:0.4em;'>
            <i class="fa-solid fa-download" aria-hidden="true" title="Download"></i>
          </div>
          `;
        else
          return ``;
      }, cellClick: (e: any, cell: any) => this.OnClickAcao(e, cell)
    }
  ];

  constructor() {
    this.config.notFoundText = "Não encontrado";

    this.userLogado = this.authService.getUser();

    if (this.userLogado != null) {
      this.atribuicoesLogin = this.userLogado?.DadosUsuario[0];
    }
  }

  ngOnInit(): void {
    this.inicializar();
  }

  private async inicializar(): Promise<void> {
    this.formGerar = new FormGroup({
      codigoPolo: new FormControl(null, [Validators.required]),
      dataInicial: new FormControl(null, [Validators.required]),
      dataFinal: new FormControl(null, [Validators.required]),
    });

    if (this.atribuicoesLogin.NumeroComportamento == 1) {
      await this.getPolosAdmin();
    }

    if (this.atribuicoesLogin.NumeroComportamento == 2) {
      await this.getPolosPerfilDiretor();
    }

    await this.ObterRelatoriosGerados();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.spinnerService.hide();
    }
  }

  public async ObterRelatoriosGerados() {
    this.spinnerService.show();

    const response = await lastValueFrom(this.relatorioService.getAtividadesRelatorio(new Atividade()))
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });

    if (!response.isSucess) {
      this.toastr.error(response.message);
      this.spinnerService.hide();
      return;
    }

    this.spinnerService.hide();
    this.relatoriosGerados = response.data;
  }

  public async GerarRelatorio() {
    this.submitted = true;

    if (this.formGerar.invalid)
      return;

    if (new Date(this.dataInicial.value) > new Date(this.dataFinal.value)) {
      this.toastr.warning('A data inicial não pode ser maior que a data final.');
      return;
    }

    this.showModalConfirmacao = true;
  }

  public async ConfirmarGeracao() {
    this.spinnerService.show();

    const parametros = new Atividade();
    parametros.codigoPolo = this.form?.codigoPolo.value;
    parametros.dataInicial = this.form?.dataInicial?.value;
    parametros.dataFinal = this.form?.dataFinal?.value;
    parametros.codigoPerfil = this.atribuicoesLogin.CodigoPerfil;
    parametros.nomePerfil = this.atribuicoesLogin.NomePerfil;

    const result = await lastValueFrom(this.relatorioService.setAtividadesRelatorio(parametros))
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });

    if (!result.isSucess) {
      this.toastr.error(result.message, result.title);
      this.spinnerService.hide();
      this.showModalConfirmacao = false;
      return;
    }

    this.spinnerService.hide();
    this.toastr.success('Relatório gerado com sucesso.');
    this.showModalConfirmacao = false;

    setTimeout(async () => {
      this.submitted = false;
      await this.ObterRelatoriosGerados();
    }, 1000);
  }

  private async OnClickAcao(e: any, cell: any) {
    const row = cell.getRow().getData();

    if (row.status === 3 && e.target.classList.contains("fa-download")) {
      const parametros = new Atividade();
      parametros.token = row.token;
      parametros.fileName = row.fileName;

      this.relatorioService.downloadRelatividade(parametros).subscribe((data) => {
        saveAs(data, row.fileName);
      });
    }
  }

  public FecharModalConfirmacao() {
    this.showModalConfirmacao = false;
  }

  public async OnChangePolo() {
    const codigoPolo = Number(this.formGerar.get('codigoPolo')?.value ?? 0);

    if (this.userLogado !== null) {
      this.userLogado.codigoPolo = codigoPolo > 0 ? codigoPolo : 0;
      this.authService.setUser(this.userLogado);
    }
  }

  public ativaPolo() {
    if (!this.polos?.length) {
      this.formGerar.get('codigoPolo')?.setValue(null);
      this.formGerar.get('codigoPolo')?.enable();
      return;
    }

    if (this.polos.length === 1) {
      const codigoPoloUnico = Number(this.polos[0].codigoPolo);

      this.formGerar.get('codigoPolo')?.setValue(codigoPoloUnico);
      this.formGerar.get('codigoPolo')?.disable();

      if (this.userLogado !== null) {
        this.userLogado.codigoPolo = codigoPoloUnico;
        this.authService.setUser(this.userLogado);
      }

      return;
    }

    const codigoAtual = Number(this.formGerar.get('codigoPolo')?.value ?? 0);
    const codigoAtualEhValido = this.polos.some(p => Number(p.codigoPolo) === codigoAtual);

    this.formGerar.get('codigoPolo')?.enable();
    this.formGerar.get('codigoPolo')?.setValue(codigoAtualEhValido ? codigoAtual : null);
  }

  private obterCodigosPolosVinculadosUsuario(): number[] {
    const dadosUsuario = this.userLogado?.DadosUsuario ?? [];

    const codigos = dadosUsuario
      .flatMap((item: any) => {
        const polos =
          item?.Polos ??
          item?.polos ??
          item?.ListaPolos ??
          item?.listaPolos;

        if (Array.isArray(polos) && polos.length > 0) {
          return polos;
        }

        return [item];
      })
      .map((item: any) =>
        Number(
          item?.CodigoPolo ??
          item?.codigoPolo ??
          item?.CdPolo ??
          item?.cdPolo ??
          item?.Polo?.CodigoPolo ??
          item?.polo?.codigoPolo ??
          0
        )
      )
      .filter((codigo: number) => codigo > 0);

    const codigoPoloLogin = Number(this.userLogado?.codigoPolo ?? 0);

    if (codigoPoloLogin > 0) {
      codigos.push(codigoPoloLogin);
    }

    return [...new Set(codigos)];
  }

  private aplicarRegraPolos(polosRetornados: Polo[]): void {
    const codigosVinculados = this.obterCodigosPolosVinculadosUsuario();

    this.polos = (polosRetornados ?? []).filter((p: Polo) =>
      codigosVinculados.includes(Number(p.codigoPolo))
    );

    this.usuarioPossuiMultiplosPolos = this.polos.length > 1;

    this.ativaPolo();
  }

  public async getPolosAdmin() {
    this.spinnerService.show();

    const response = await lastValueFrom(this.psicologoService.getPolos())
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });

    if (!response.isSucess) {
      this.toastr.error(response.message);
      this.spinnerService.hide();
      return;
    }

    this.aplicarRegraPolos(response.data);
    this.spinnerService.hide();
  }

  public async getPolosPerfilDiretor() {
    this.spinnerService.show();

    const response = await lastValueFrom(this.psicologoService.getPolos())
      .catch((err) => {
        return this.ConvivaErrors.handleResultError(err);
      });

    if (!response.isSucess) {
      this.toastr.error(response.message);
      this.spinnerService.hide();
      return;
    }

    this.aplicarRegraPolos(response.data);
    this.spinnerService.hide();
  }
}
